import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import '@/styles/tactical-ui.css';
import usePageTitle from "@/hooks/usePageTitle";

// COMPREHENSIVE GLOBAL COUNTRY CODES (150+ countries, Qatar prioritized)
const COUNTRY_CODES = [
  // PRIORITY: Qatar first
  { code: '+974', country: 'QA', flag: '🇶🇦', name: 'Qatar' },
  
  // Gulf Cooperation Council (GCC)
  { code: '+971', country: 'AE', flag: '🇦🇪', name: 'UAE' },
  { code: '+966', country: 'SA', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+965', country: 'KW', flag: '🇰🇼', name: 'Kuwait' },
  { code: '+973', country: 'BH', flag: '🇧🇭', name: 'Bahrain' },
  { code: '+968', country: 'OM', flag: '🇴🇲', name: 'Oman' },
  
  // Major International Markets
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States' },
  { code: '+1', country: 'CA', flag: '🇨🇦', name: 'Canada' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China' },
  { code: '+81', country: 'JP', flag: '🇯🇵', name: 'Japan' },
  { code: '+82', country: 'KR', flag: '🇰🇷', name: 'South Korea' },
  { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia' },
  { code: '+64', country: 'NZ', flag: '🇳🇿', name: 'New Zealand' },
  { code: '+65', country: 'SG', flag: '🇸🇬', name: 'Singapore' },
  
  // Europe
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italy' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain' },
  { code: '+31', country: 'NL', flag: '🇳🇱', name: 'Netherlands' },
  { code: '+41', country: 'CH', flag: '🇨🇭', name: 'Switzerland' },
  { code: '+46', country: 'SE', flag: '🇸🇪', name: 'Sweden' },
  { code: '+47', country: 'NO', flag: '🇳🇴', name: 'Norway' },
  { code: '+45', country: 'DK', flag: '🇩🇰', name: 'Denmark' },
  { code: '+358', country: 'FI', flag: '🇫🇮', name: 'Finland' },
  { code: '+48', country: 'PL', flag: '🇵🇱', name: 'Poland' },
  { code: '+7', country: 'RU', flag: '🇷🇺', name: 'Russia' },
  { code: '+380', country: 'UA', flag: '🇺🇦', name: 'Ukraine' },
  { code: '+90', country: 'TR', flag: '🇹🇷', name: 'Turkey' },
  { code: '+30', country: 'GR', flag: '🇬🇷', name: 'Greece' },
  { code: '+351', country: 'PT', flag: '🇵🇹', name: 'Portugal' },
  { code: '+353', country: 'IE', flag: '🇮🇪', name: 'Ireland' },
  { code: '+32', country: 'BE', flag: '🇧🇪', name: 'Belgium' },
  { code: '+43', country: 'AT', flag: '🇦🇹', name: 'Austria' },
  { code: '+420', country: 'CZ', flag: '🇨🇿', name: 'Czech Republic' },
  { code: '+36', country: 'HU', flag: '🇭🇺', name: 'Hungary' },
  { code: '+40', country: 'RO', flag: '🇷🇴', name: 'Romania' },
  
  // Middle East & North Africa
  { code: '+20', country: 'EG', flag: '🇪🇬', name: 'Egypt' },
  { code: '+212', country: 'MA', flag: '🇲🇦', name: 'Morocco' },
  { code: '+213', country: 'DZ', flag: '🇩🇿', name: 'Algeria' },
  { code: '+216', country: 'TN', flag: '🇹🇳', name: 'Tunisia' },
  { code: '+218', country: 'LY', flag: '🇱🇾', name: 'Libya' },
  { code: '+962', country: 'JO', flag: '🇯🇴', name: 'Jordan' },
  { code: '+961', country: 'LB', flag: '🇱🇧', name: 'Lebanon' },
  { code: '+963', country: 'SY', flag: '🇸🇾', name: 'Syria' },
  { code: '+964', country: 'IQ', flag: '🇮🇶', name: 'Iraq' },
  { code: '+98', country: 'IR', flag: '🇮🇷', name: 'Iran' },
  { code: '+972', country: 'IL', flag: '🇮🇱', name: 'Israel' },
  { code: '+967', country: 'YE', flag: '🇾🇪', name: 'Yemen' },
  
  // Africa
  { code: '+27', country: 'ZA', flag: '🇿🇦', name: 'South Africa' },
  { code: '+234', country: 'NG', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+254', country: 'KE', flag: '🇰🇪', name: 'Kenya' },
  { code: '+233', country: 'GH', flag: '🇬🇭', name: 'Ghana' },
  { code: '+255', country: 'TZ', flag: '🇹🇿', name: 'Tanzania' },
  { code: '+256', country: 'UG', flag: '🇺🇬', name: 'Uganda' },
  { code: '+251', country: 'ET', flag: '🇪🇹', name: 'Ethiopia' },
  { code: '+225', country: 'CI', flag: '🇨🇮', name: 'Ivory Coast' },
  { code: '+237', country: 'CM', flag: '🇨🇲', name: 'Cameroon' },
  { code: '+221', country: 'SN', flag: '🇸🇳', name: 'Senegal' },
  
  // Asia-Pacific
  { code: '+60', country: 'MY', flag: '🇲🇾', name: 'Malaysia' },
  { code: '+66', country: 'TH', flag: '🇹🇭', name: 'Thailand' },
  { code: '+63', country: 'PH', flag: '🇵🇭', name: 'Philippines' },
  { code: '+62', country: 'ID', flag: '🇮🇩', name: 'Indonesia' },
  { code: '+84', country: 'VN', flag: '🇻🇳', name: 'Vietnam' },
  { code: '+880', country: 'BD', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+92', country: 'PK', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+94', country: 'LK', flag: '🇱🇰', name: 'Sri Lanka' },
  { code: '+95', country: 'MM', flag: '🇲🇲', name: 'Myanmar' },
  { code: '+855', country: 'KH', flag: '🇰🇭', name: 'Cambodia' },
  { code: '+856', country: 'LA', flag: '🇱🇦', name: 'Laos' },
  { code: '+977', country: 'NP', flag: '🇳🇵', name: 'Nepal' },
  { code: '+852', country: 'HK', flag: '🇭🇰', name: 'Hong Kong' },
  { code: '+853', country: 'MO', flag: '🇲🇴', name: 'Macau' },
  { code: '+886', country: 'TW', flag: '🇹🇼', name: 'Taiwan' },
  
  // Americas
  { code: '+52', country: 'MX', flag: '🇲🇽', name: 'Mexico' },
  { code: '+55', country: 'BR', flag: '🇧🇷', name: 'Brazil' },
  { code: '+54', country: 'AR', flag: '🇦🇷', name: 'Argentina' },
  { code: '+56', country: 'CL', flag: '🇨🇱', name: 'Chile' },
  { code: '+57', country: 'CO', flag: '🇨🇴', name: 'Colombia' },
  { code: '+51', country: 'PE', flag: '🇵🇪', name: 'Peru' },
  { code: '+58', country: 'VE', flag: '🇻🇪', name: 'Venezuela' },
  { code: '+593', country: 'EC', flag: '🇪🇨', name: 'Ecuador' },
  { code: '+507', country: 'PA', flag: '🇵🇦', name: 'Panama' },
  { code: '+506', country: 'CR', flag: '🇨🇷', name: 'Costa Rica' },
  { code: '+53', country: 'CU', flag: '🇨🇺', name: 'Cuba' },
  { code: '+1', country: 'JM', flag: '🇯🇲', name: 'Jamaica' },
  
  // Caribbean
  { code: '+1', country: 'TT', flag: '🇹🇹', name: 'Trinidad and Tobago' },
  { code: '+1', country: 'BB', flag: '🇧🇧', name: 'Barbados' },
  { code: '+1', country: 'BS', flag: '🇧🇸', name: 'Bahamas' },
  
  // Central Asia
  { code: '+7', country: 'KZ', flag: '🇰🇿', name: 'Kazakhstan' },
  { code: '+998', country: 'UZ', flag: '🇺🇿', name: 'Uzbekistan' },
  { code: '+996', country: 'KG', flag: '🇰🇬', name: 'Kyrgyzstan' },
  { code: '+992', country: 'TJ', flag: '🇹🇯', name: 'Tajikistan' },
  { code: '+993', country: 'TM', flag: '🇹🇲', name: 'Turkmenistan' },
  { code: '+374', country: 'AM', flag: '🇦🇲', name: 'Armenia' },
  { code: '+994', country: 'AZ', flag: '🇦🇿', name: 'Azerbaijan' },
  { code: '+995', country: 'GE', flag: '🇬🇪', name: 'Georgia' },
  
  // Additional European
  { code: '+375', country: 'BY', flag: '🇧🇾', name: 'Belarus' },
  { code: '+373', country: 'MD', flag: '🇲🇩', name: 'Moldova' },
  { code: '+381', country: 'RS', flag: '🇷🇸', name: 'Serbia' },
  { code: '+385', country: 'HR', flag: '🇭🇷', name: 'Croatia' },
  { code: '+386', country: 'SI', flag: '🇸🇮', name: 'Slovenia' },
  { code: '+387', country: 'BA', flag: '🇧🇦', name: 'Bosnia' },
  { code: '+389', country: 'MK', flag: '🇲🇰', name: 'North Macedonia' },
  { code: '+383', country: 'XK', flag: '🇽🇰', name: 'Kosovo' },
  { code: '+355', country: 'AL', flag: '🇦🇱', name: 'Albania' },
  { code: '+382', country: 'ME', flag: '🇲🇪', name: 'Montenegro' },
  { code: '+354', country: 'IS', flag: '🇮🇸', name: 'Iceland' },
  { code: '+372', country: 'EE', flag: '🇪🇪', name: 'Estonia' },
  { code: '+371', country: 'LV', flag: '🇱🇻', name: 'Latvia' },
  { code: '+370', country: 'LT', flag: '🇱🇹', name: 'Lithuania' },
  { code: '+421', country: 'SK', flag: '🇸🇰', name: 'Slovakia' },
  { code: '+359', country: 'BG', flag: '🇧🇬', name: 'Bulgaria' },
  { code: '+357', country: 'CY', flag: '🇨🇾', name: 'Cyprus' },
  { code: '+356', country: 'MT', flag: '🇲🇹', name: 'Malta' },
  { code: '+352', country: 'LU', flag: '🇱🇺', name: 'Luxembourg' },
];

// Language options - NO EMOJIS (Safari iOS compatibility issue)
// Emojis in <option> tags cause rendering failures on mobile Safari
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ar', name: 'العربية (Arabic)' },
  { code: 'es', name: 'Español (Spanish)' },
  { code: 'fr', name: 'Français (French)' },
  { code: 'de', name: 'Deutsch (German)' },
  { code: 'it', name: 'Italiano (Italian)' },
  { code: 'pt', name: 'Português (Portuguese)' },
  { code: 'zh', name: '中文 (Chinese)' },
  { code: 'ja', name: '日本語 (Japanese)' },
  { code: 'hi', name: 'हिन्दी (Hindi)' },
];

// Password strength calculator
const calculatePasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 6) strength++;
  if (password.length >= 10) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;
  return strength;
};

const getStrengthLabel = (strength) => {
  if (strength === 0) return { label: '', color: '' };
  if (strength <= 1) return { label: 'Weak', color: 'bg-red-500' };
  if (strength <= 3) return { label: 'Fair', color: 'bg-yellow-500' };
  if (strength <= 4) return { label: 'Good', color: 'bg-blue-500' };
  return { label: 'Strong', color: 'bg-green-500' };
};

const Signup = () => {
  usePageTitle("pageTitles.signup");
  const navigate = useNavigate();
  const { signUp, supabase } = useAuth();
  
  // Extended form fields
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState('India'); // Default to India (will be auto-detected)
  const [countryCode, setCountryCode] = useState('+91'); // Default to India (will be auto-detected)
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Initialize language from landing page preference or default to 'en'
  const [preferredLanguage, setPreferredLanguage] = useState(() => {
    return localStorage.getItem('plateprofit_signup_language') || 
           localStorage.getItem('plateprofit_landing_language') || 
           'en';
  });
  
  // Chef Persona fields
  const [gender, setGender] = useState('');
  const [professionalIdentity, setProfessionalIdentity] = useState('');
  const [userGoals, setUserGoals] = useState([]);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Password validation state
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  
  // Searchable dropdown state
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Email verification state
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [userId, setUserId] = useState(null);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Real-time password matching logic
  useEffect(() => {
    if (confirmPassword.length > 0) {
      setPasswordsMatch(password === confirmPassword);
    }
  }, [password, confirmPassword]);

  // Real-time password strength calculation
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  // Smart Geolocation: Auto-detect user's country on page load
  useEffect(() => {
    // Diagnostic: Verify LANGUAGES array is available (mobile Safari debugging)
    console.log('🔍 LANGUAGES array loaded:', LANGUAGES.length, 'languages available');
    
    const detectUserLocation = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/', { timeout: 5000 });
        if (response.ok) {
          const data = await response.json();
          const detectedCountryCode = data.country_code; // e.g., "IN", "QA", "US"
          
          // Find matching country in our COUNTRY_CODES array
          const matchedCountry = COUNTRY_CODES.find(c => c.country === detectedCountryCode);
          
          if (matchedCountry) {
            console.log('🌍 Auto-detected country:', matchedCountry.name);
            setCountryCode(matchedCountry.code);
            setCountry(matchedCountry.name);
          } else {
            // Fallback to India if country not found in our list
            console.log('⚠️ Country not found, defaulting to India');
            setCountryCode('+91');
            setCountry('India');
          }
        }
      } catch (error) {
        // Fallback to India (+91) if geolocation fails
        console.log('⚠️ Geolocation failed, defaulting to India:', error);
        setCountryCode('+91');
        setCountry('India');
      }
    };
    
    detectUserLocation();
  }, []); // Run once on component mount

  // Filter country codes based on search
  const filteredCountryCodes = COUNTRY_CODES.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.code.includes(searchTerm) ||
    item.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected country display
  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];

  // Phone number validation based on country
  const validatePhoneNumber = (phone, code) => {
    const digitsOnly = phone.replace(/\D/g, '');
    
    switch(code) {
      case '+974': return digitsOnly.length === 8;
      case '+971': return digitsOnly.length === 9;
      case '+966': return digitsOnly.length === 9;
      case '+965': return digitsOnly.length === 8;
      case '+973': return digitsOnly.length === 8;
      case '+968': return digitsOnly.length === 8;
      case '+1': return digitsOnly.length === 10;
      case '+44': return digitsOnly.length === 10;
      case '+91': return digitsOnly.length === 10;
      default: return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    }
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    return (
      fullName.trim() &&
      country.trim() &&
      phoneNumber.trim() &&
      validatePhoneNumber(phoneNumber, countryCode) &&
      gender && // Required
      professionalIdentity && // Required
      userGoals.length > 0 && // At least one goal
      email.trim() &&
      password.length >= 6 &&
      passwordsMatch
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isFormValid()) {
      setError('Please fill all fields correctly');
      return;
    }

    setLoading(true);

    try {
      const fullMobileNumber = `${countryCode} ${phoneNumber}`;
      
      console.log('🚀 Starting signup with data:', {
        email,
        full_name: fullName,
        country,
        gender,
        professional_identity: professionalIdentity,
        user_goals: userGoals
      });
      
      // CRITICAL FIX: Send ONLY essential data to Supabase Auth
      // Chef persona data will be sent to backend /api/settings after signup
      const signUpResult = await signUp(email, password, {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName, // Keep only essential metadata
        }
      });

      console.log('📩 Signup result:', signUpResult);

      // Check error FIRST - Supabase has already parsed it
      if (signUpResult.error) {
        console.error('❌ Signup error:', signUpResult.error);
        // Display user-friendly error messages
        if (signUpResult.error.message?.includes('already registered')) {
          setError('This email is already registered. Please sign in instead.');
        } else if (signUpResult.error.message?.includes('Password')) {
          setError('Password is too weak. Please use at least 6 characters with mixed case and numbers.');
        } else {
          setError(signUpResult.error.message || 'Failed to create account. Please try again.');
        }
        return; // Stop here - don't proceed
      }

      const { data } = signUpResult;

      // Only access data if no error
      if (data?.user) {
        setUserId(data.user.id);
        console.log('✅ User created:', data.user.id);
        
        // Create profile with extended data
        const API_URL = import.meta.env.VITE_REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
        const token = data.session?.access_token;
        
        if (token) {
          console.log('🔑 Creating profile with token...');
          try {
            const profileData = {
              full_name: fullName,
              country: country,
              mobile_number: fullMobileNumber,
              preferred_language: preferredLanguage,
              gender: gender,
              professional_identity: professionalIdentity,
              user_goals: userGoals // Send as array to backend
            };
            
            console.log('📤 Sending profile data:', profileData);
            
            const profileResponse = await axios.put(`${API_URL}/api/settings`, profileData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            console.log('✅ Profile created:', profileResponse.data);
          } catch (profileError) {
            console.error('⚠️ Profile creation error:', profileError);
            console.error('Error details:', {
              message: profileError.message,
              response: profileError.response?.data,
              status: profileError.response?.status
            });
            // Continue anyway - profile will be created on first login
          }
        }
        
        console.log('✅ Signup complete, showing OTP screen');
        setShowOTPInput(true);
        setError('');
        
        // UX Enhancement: Scroll to top for "new page" transition
        window.scrollTo(0, 0);
      }
    } catch (err) {
      // Catch any unexpected errors
      console.error('💥 Unexpected signup error:', err);
      console.error('Error stack:', err.stack);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Verify OTP - Single call, no double reads
      const { data, error } = await supabase.auth.verifyOtp({
        email: email,
        token: otpCode,
        type: 'signup'
      });

      // Check for error first (no data reading yet)
      if (error) {
        throw error;
      }

      // Now safely access data (only once)
      if (data?.session) {
        const API_URL = import.meta.env.VITE_REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
        
        // Small delay to let auth state settle
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
          // Update email_verified status (uses cached session from interceptor)
          await axios.put(`${API_URL}/api/settings`, {
            email_verified: true
          }, {
            headers: {
              'Authorization': `Bearer ${data.session.access_token}`
            }
          });
        } catch (err) {
          console.error('Email verification status update failed:', err);
          // Continue anyway - user can still login
        }

        // Small delay before navigation to prevent race
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Redirect to dashboard
        navigate('/');
      }
    } catch (err) {
      // Error object is already parsed - no body stream reading
      console.error('OTP verification error:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });

      if (error) throw error;
      
      setError(''); // Clear any errors
      // Success feedback
      const successMsg = '✅ Verification code resent! Check your email (including spam folder).';
      alert(successMsg);
      console.log('OTP Resent to:', email);
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // DEVELOPMENT MODE: Skip email verification for testing
  const handleSkipVerification = async () => {
    const isDevMode = window.location.hostname.includes('preview') || window.location.hostname.includes('localhost');
    
    if (!isDevMode) {
      setError('Skip verification is only available in development mode.');
      return;
    }

    setLoading(true);
    console.log('🚧 DEV MODE: Skipping email verification for', email);
    
    try {
      // Sign in directly with the credentials
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (error) {
        console.error('Skip verification - signin error:', error);
        setError('Account created but not verified. Please check your email or contact support.');
        setLoading(false);
        return;
      }

      if (data?.session) {
        console.log('✅ Signed in successfully, creating profile...');
        
        // Create/update profile with ALL fields
        const API_URL = import.meta.env.VITE_REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
        const fullMobileNumber = `${countryCode} ${phoneNumber}`;
        
        try {
          await axios.put(`${API_URL}/api/settings`, {
            full_name: fullName,
            country: country,
            mobile_number: fullMobileNumber,
            preferred_language: preferredLanguage,
            gender: gender,
            professional_identity: professionalIdentity,
            user_goals: userGoals,
            email_verified: true  // Mark as verified in dev mode
          }, {
            headers: {
              'Authorization': `Bearer ${data.session.access_token}`
            }
          });
          
          console.log('✅ Profile created successfully');
        } catch (profileErr) {
          console.error('Profile creation error:', profileErr);
          // Continue anyway
        }

        // Small delay to ensure profile is saved
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('✅ Redirecting to dashboard...');
        navigate('/');
      }
    } catch (err) {
      console.error('Skip verification error:', err);
      setError('Unable to skip verification. Please use the OTP from your email.');
    } finally {
      setLoading(false);
    }
  };

  const strengthInfo = getStrengthLabel(passwordStrength);

  // Show OTP verification form
  if (showOTPInput) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-amber-500 mb-2">Verify Your Email</h1>
            <p className="text-slate-400 text-lg">
              We sent a 6-digit code to <strong className="text-white">{email}</strong>
            </p>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <label className="block text-slate-300 mb-2 font-medium">Verification Code</label>
              <input
                type="text"
                className="auth-input text-center text-2xl tracking-widest"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                placeholder="000000"
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading || otpCode.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>

            {/* Resend OTP Button */}
            <button
              type="button"
              onClick={handleResendOTP}
              className="w-full py-3 text-amber-500 hover:text-amber-400 font-semibold transition-colors"
              disabled={loading}
            >
              📧 Resend Code
            </button>
          </form>

          <div className="mt-6 p-4 bg-slate-800 rounded-lg border border-slate-700">
            <p className="text-slate-400 text-sm">
              <strong className="text-white">Not receiving emails?</strong>
              <br />
              • Check your spam/junk folder
              <br />
              • Verify email: <span className="text-amber-400">{email}</span>
              <br />
              • Click "Resend Code" to try again
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show signup form
  return (
    <div className="auth-container">
      <div className="auth-card max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-amber-500 mb-2">PlateProfit</h1>
          <p className="text-slate-400 text-lg">Create your verified chef account</p>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-slate-300 mb-2 font-medium">
              Full Name <span className="text-amber-500">*</span>
            </label>
            <input
              type="text"
              className="auth-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              placeholder="Nasser Al-Khelaifi"
              autoComplete="name"
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-slate-300 mb-2 font-medium">
              Country <span className="text-amber-500">*</span>
            </label>
            <input
              type="text"
              className="auth-input"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              placeholder="Qatar"
              autoComplete="country-name"
            />
          </div>

          {/* Mobile Number - Searchable Country Code Dropdown */}
          <div>
            <label className="block text-slate-300 mb-2 font-medium">
              Mobile Number <span className="text-amber-500">*</span>
            </label>
            <div className="flex gap-3">
              <div className="relative w-40">
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="auth-input w-full text-left flex items-center justify-between"
                >
                  <span>{selectedCountry.flag} {selectedCountry.code}</span>
                  <span className="text-slate-500">▼</span>
                </button>
                
                {showDropdown && (
                  <div className="absolute z-50 w-80 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-80 overflow-hidden">
                    <div className="p-2 border-b border-slate-700 sticky top-0 bg-slate-800">
                      <input
                        type="text"
                        className="w-full px-3 py-2 bg-slate-900 text-white rounded border border-slate-600 focus:border-amber-500 focus:outline-none text-sm"
                        placeholder="Search country or code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                    
                    <div className="overflow-y-auto max-h-64">
                      {filteredCountryCodes.map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setCountryCode(item.code);
                            setShowDropdown(false);
                            setSearchTerm('');
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-slate-700 flex items-center gap-2 text-sm text-white"
                        >
                          <span className="text-xl">{item.flag}</span>
                          <span className="flex-1">{item.name}</span>
                          <span className="text-slate-400">{item.code}</span>
                        </button>
                      ))}
                      {filteredCountryCodes.length === 0 && (
                        <div className="px-4 py-3 text-slate-400 text-sm text-center">
                          No countries found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <input
                type="tel"
                className="auth-input flex-1"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s()-]/g, ''))}
                required
                placeholder={countryCode === '+974' ? '3123 4567' : '1234567890'}
                autoComplete="tel-national"
              />
            </div>
            <p className="text-slate-500 text-sm mt-1">
              Combined: {countryCode} {phoneNumber}
              {countryCode === '+974' && phoneNumber && (
                <span className="ml-2">
                  {validatePhoneNumber(phoneNumber, countryCode) ? '✓' : '(8 digits required)'}
                </span>
              )}
            </p>
          </div>

          {/* Preferred Language */}
          <div>
            <label className="block text-slate-300 mb-2 font-medium">
              Preferred Language <span className="text-amber-500">*</span>
            </label>
            <select
              className="auth-input text-base"
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              required
            >
              {/* HARDCODED - No conditionals, no .map(), direct options for Safari iOS */}
              <option value="en">English</option>
              <option value="ar">العربية (Arabic)</option>
              <option value="es">Español (Spanish)</option>
              <option value="fr">Français (French)</option>
              <option value="de">Deutsch (German)</option>
              <option value="it">Italiano (Italian)</option>
              <option value="pt">Português (Portuguese)</option>
              <option value="zh">中文 (Chinese)</option>
              <option value="ja">日本語 (Japanese)</option>
              <option value="hi">हिन्दी (Hindi)</option>
            </select>
          </div>

          {/* Chef Persona Section */}
          <div className="border-t border-slate-700 pt-6 mt-6">
            <h2 className="text-xl font-bold text-white mb-4">👨‍🍳 Tell Us About Yourself</h2>
            
            {/* Gender */}
            <div className="mb-6">
              <label className="block text-slate-300 mb-2 font-medium">
                Gender <span className="text-amber-500">*</span>
              </label>
              <select
                className="auth-input text-base"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                required
              >
                <option value="">Select gender...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>

            {/* Professional Identity */}
            <div className="mb-6">
              <label className="block text-slate-300 mb-2 font-medium">
                I am a... <span className="text-amber-500">*</span>
              </label>
              <select
                className="auth-input text-base"
                value={professionalIdentity}
                onChange={(e) => setProfessionalIdentity(e.target.value)}
                required
              >
                <option value="">Select your role...</option>
                <option value="home-cook">🏠 Home Cook</option>
                <option value="professional-chef">👨‍🍳 Professional Chef</option>
                <option value="pastry-artist">🧁 Pastry Artist</option>
                <option value="mixologist">🍸 Mixologist</option>
                <option value="spice-maker">🌶️ Spice Maker</option>
                <option value="food-vendor">🍔 Food Vendor</option>
              </select>
            </div>

            {/* User Goals - Multi-select */}
            <div className="mb-6">
              <label className="block text-slate-300 mb-3 font-medium">
                What do you want to do with PlateProfit? <span className="text-amber-500">*</span>
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:border-amber-500 transition-colors">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-amber-500 bg-slate-700 border-slate-600 rounded focus:ring-amber-500"
                    checked={userGoals.includes('store-recipes')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setUserGoals([...userGoals, 'store-recipes']);
                      } else {
                        setUserGoals(userGoals.filter(g => g !== 'store-recipes'));
                      }
                    }}
                  />
                  <span className="text-white">📋 Store & Organize My Recipes</span>
                </label>
                
                <label className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:border-amber-500 transition-colors">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-amber-500 bg-slate-700 border-slate-600 rounded focus:ring-amber-500"
                    checked={userGoals.includes('price-menus')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setUserGoals([...userGoals, 'price-menus']);
                      } else {
                        setUserGoals(userGoals.filter(g => g !== 'price-menus'));
                      }
                    }}
                  />
                  <span className="text-white">💰 Price Menus & Calculate Profits</span>
                </label>
              </div>
              {userGoals.length === 0 && (
                <p className="text-red-400 text-sm mt-2">Please select at least one goal</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-slate-300 mb-2 font-medium">
              Email <span className="text-amber-500">*</span>
            </label>
            <input
              type="email"
              className="auth-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="chef@restaurant.qa"
            />
          </div>

          {/* Password with Strength Indicator */}
          <div>
            <label className="block text-slate-300 mb-2 font-medium">
              Password <span className="text-amber-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="auth-input pr-12"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {/* Password Strength Bar */}
            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-1 flex-1 rounded ${
                        level <= passwordStrength ? strengthInfo.color : 'bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
                <p className={`text-sm ${
                  passwordStrength <= 1 ? 'text-red-400' :
                  passwordStrength <= 3 ? 'text-yellow-400' :
                  passwordStrength <= 4 ? 'text-blue-400' : 'text-green-400'
                }`}>
                  {strengthInfo.label} {password.length < 6 && '(minimum 6 characters)'}
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password with Real-time Matching */}
          <div>
            <label className="block text-slate-300 mb-2 font-medium">
              Confirm Password <span className="text-amber-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className={`auth-input pr-12 ${
                  confirmTouched && confirmPassword.length > 0
                    ? passwordsMatch
                      ? 'border-green-500 focus:border-green-500'
                      : 'border-red-500 focus:border-red-500'
                    : ''
                }`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setConfirmTouched(true)}
                required
                autoComplete="new-password"
                placeholder="••••••••"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {confirmTouched && confirmPassword.length > 0 && (
                  <span>
                    {passwordsMatch ? (
                      <Check size={20} className="text-green-500" />
                    ) : (
                      <X size={20} className="text-red-500" />
                    )}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-slate-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            {/* Real-time Match Feedback */}
            {confirmTouched && confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                <X size={14} /> Passwords do not match
              </p>
            )}
            {confirmTouched && passwordsMatch && confirmPassword.length > 0 && (
              <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                <Check size={14} /> Passwords match
              </p>
            )}
          </div>

          <button
            type="submit"
            className="auth-button disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !isFormValid()}
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-amber-500 hover:text-amber-400 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
